import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment } from "react";
import { Link, useParams } from "react-router";
import { categoryAnchor, conditionAnchor } from "../lib/links";
import type { TaxonomyDoc } from "../sources/taxonomy";
import { useManifestStore } from "../state/manifestStore";
import { useSettings } from "../state/settingsStore";
import { ConditionDetail } from "./ConditionDetail";

export function ConditionsTab({
  tax,
  taxError,
  openConds,
  toggleCond,
}: {
  tax: TaxonomyDoc | null;
  taxError: string | null;
  openConds: Set<string>;
  toggleCond: (code: string) => void;
}) {
  const { tenant } = useParams();
  const manifest = useManifestStore((s) => s.manifest);
  const syntheticOnly = useSettings((s) => s.syntheticOnly);

  if (taxError) return <div className="coming-soon">Couldn't load taxonomy: {taxError}</div>;
  if (!tax) return <div className="coming-soon">Loading taxonomy…</div>;
  if (!tenant || !manifest) return null;

  const counts = new Map<string, number>();
  for (const s of manifest.sessions) {
    if (s.tenant !== tenant) continue;
    if (syntheticOnly && !s.synthetic) continue;
    for (const c of s.top_conditions) {
      counts.set(c.code, (counts.get(c.code) ?? 0) + c.count);
    }
  }
  const rows = tax.conditions.map((entry) => ({
    entry,
    count: counts.get(entry.code) ?? 0,
  }));
  // Sort: observed (count > 0) first by count desc, then unobserved alphabetical by code.
  rows.sort((a, b) => {
    if (a.count !== b.count) return b.count - a.count;
    return a.entry.code.localeCompare(b.entry.code);
  });

  return (
    <div className="card no-pad">
      <table className="roster condition-table">
        <thead>
          <tr>
            <th aria-label="expand" />
            <th>Code</th>
            <th>Short</th>
            <th>Condition</th>
            <th>Category</th>
            <th>Sessions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ entry, count }) => {
            const isOpen = openConds.has(entry.code);
            return (
              <Fragment key={entry.code}>
                <tr
                  id={conditionAnchor(entry.code)}
                  className={`condition-row ${isOpen ? "expanded" : ""} ${entry.has_observer ? "" : "no-observer"}`}
                  onClick={() => toggleCond(entry.code)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") toggleCond(entry.code);
                  }}
                  tabIndex={0}
                >
                  <td className="chev-cell">
                    <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} fixedWidth />
                  </td>
                  <td className="mono">{entry.code}</td>
                  <td className="mono">{entry.short_name}</td>
                  <td>{entry.name}</td>
                  <td>
                    {entry.category ? (
                      <Link
                        to={`/${tenant}/conditions#${categoryAnchor(entry.category)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="cat-link"
                      >
                        {entry.category}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="mono">{count > 0 ? count : <span className="dim">—</span>}</td>
                </tr>
                {isOpen ? (
                  <tr className="condition-detail-row">
                    <td colSpan={6}>
                      <ConditionDetail entry={entry} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
